import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import { Badge, Switch } from "../../../components/ui";
import { getPaymentMethods, updatePaymentMethods } from "../../../services/payments";
import { can } from "../../../utils/roles";

const METHODS = [
  { key: "card", label: "Card payment", detail: "Stripe, Apple Pay, Google Pay", icon: "ri-bank-card-line" },
  { key: "mobile", label: "Mobile Money", detail: "pawaPay, CinetPay, Paystack", icon: "ri-smartphone-line" },
];

/**
 * Cards of the payment methods accepted for NEW payments (site and apps), to
 * place in the gateways grid. Turning one off does not touch running
 * subscriptions; at least one method stays on.
 */
const AcceptedMethods = () => {
  const { t } = useTranslation();
  const canEdit = can("payments", "edit");
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null); // { key, enabled }

  useEffect(() => {
    getPaymentMethods()
      .then(setState)
      .catch(() => setState(null));
  }, []);

  const apply = async ({ key, enabled }) => {
    try {
      setState(await updatePaymentMethods({ [key]: enabled }));
      toast.success(enabled ? t("Payment method enabled") : t("Payment method disabled"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    }
  };

  if (!state) return null;
  const method = pending && METHODS.find((m) => m.key === pending.key);
  return (
    <>
      {METHODS.map((m) => {
        const info = state[m.key] || {};
        return (
          <section key={m.key} className="ui-card payment-gateway" data-off={info.enabled ? undefined : ""} aria-label={t(m.label)}>
            <div className="payment-gateway-head">
              <span className="payment-gateway-icon" aria-hidden="true">
                <i className={m.icon}></i>
              </span>
              <div className="payment-method-text">
                <strong>{t(m.label)}</strong>
                <span className="ui-hint">
                  {m.detail}
                  {m.key === "mobile" && info.enabled && ` · ${t("countriesOpen", { count: info.countries || 0 })}`}
                </span>
              </div>
            </div>
            <div className="payment-gateway-foot">
              {info.enabled ? <Badge color="green" dot>{t("Accepted")}</Badge> : <Badge color="red" dot>{t("Disabled")}</Badge>}
              {canEdit && (
                <Switch
                  checked={Boolean(info.enabled)}
                  label={t(m.label)}
                  onChange={(enabled) => setPending({ key: m.key, enabled })}
                />
              )}
            </div>
          </section>
        );
      })}
      <ConfirmDialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        onConfirm={() => apply(pending)}
        danger={pending ? !pending.enabled : false}
        confirmLabel={pending?.enabled ? t("Enable") : t("Disable")}
      >
        {method && (
          <>
            <strong>{pending.enabled ? t("Enable {{name}}?", { name: t(method.label) }) : t("Disable {{name}}?", { name: t(method.label) })}</strong>
            <p>
              {pending.enabled
                ? t("Customers can pay this way again right away.")
                : t("Customers will no longer be able to start a payment this way. Running subscriptions continue.")}
            </p>
          </>
        )}
      </ConfirmDialog>
    </>
  );
};

export default AcceptedMethods;
